// React side of the shop management navigation (config: shopManagementNavigation.js), used by the
// shared shop header (headerShopSeller.js) for BOTH shop websites (shopType "seller" | "provider"):
//   useShopHeaderNavigation   tool links for this viewer + the ☰ drawer registration
//   ShopManagementMenuItems   the desktop group, after the shop links:
//                               owner                              | Dashboard | Dropshipping In | My Products
//                               Seller visiting a Provider shop    | Dropshipping In | My Products (SELLER TOOLS)
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { FaStore, FaInfoCircle, FaSlidersH, FaHome } from "react-icons/fa";
import { elakayAutoText as text } from "../pageInside/dataElakay";
import { buildShopHeaderTools } from "./shopManagementNavigation";
import { setDrawerShopNav, clearDrawerShopNav } from "../../components/SIdeDrawer/drawerShopSlot";

// `siteMode`: a shop website header that carries its own ☰ button (outOffPage false). Only then is the
// shop navigation registered for the drawer (SHOP -> <TYPE> MANAGEMENT or SELLER TOOLS -> ELAKAY) and the header
// reduced to Home / Search / ☰ on small screens (headerShopSeller.css, .headershop-navbar--site).
export function useShopHeaderNavigation({ shopType, infoSource, canManageShop, outOffPage, showFilter, onFilterShop, title, homeTo, aboutTo, extraShopItems, labels = {} }) {
  // Owner: <TYPE> MANAGEMENT; Seller-capable visitor of a Provider shop: SELLER TOOLS.
  const tools = useMemo(
    () => buildShopHeaderTools({ shopType, infoSource, canManage: canManageShop }),
    [shopType, infoSource, canManageShop]
  );
  const management = tools.links;
  const history = tools.history?.links?.length ? tools.history : null;
  const siteMode = Boolean(shopType) && !outOffPage;
  const filterRef = useRef(onFilterShop);
  filterRef.current = onFilterShop;
  const extraKey = JSON.stringify((extraShopItems || []).map((i) => [i.key, i.label, i.to]));

  useEffect(() => {
    if (!siteMode) return undefined;
    const owner = `shopHeader:${shopType}`;
    setDrawerShopNav({
      owner,
      title,
      sections: [
        { key: "shop", label: text("h_psh_group_shop", "Shop"), items: [
          { key: "home", label: labels.home || text("h_psh_home", "Home"), to: homeTo, icon: FaStore },
          { key: "about", label: labels.about || text("h_psh_hero_about", "About us"), to: aboutTo, icon: FaInfoCircle },
          ...(showFilter ? [{ key: "filter", label: labels.filter || text("h_spm_filters", "Filter"), onClick: () => filterRef.current?.(), icon: FaSlidersH }] : []),
          ...(extraShopItems || []),
        ] },
        { key: "manage", label: tools.label, items: management },
        ...(history ? [{ key: "history", label: history.label, items: history.links }] : []),
        { key: "elakay", label: "Elakay", items: [{ key: "elakay-home", label: text("h_psh_elakay_home", "Elakay Home"), to: "/", icon: FaHome }] },
      ],
    });
    return () => clearDrawerShopNav(owner);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteMode, shopType, title, homeTo, aboutTo, showFilter, extraKey, tools, labels.home, labels.about, labels.filter]);

  return { management, managementLabel: tools.label, history, siteMode };
}

// "History ▾" of the header (desktop / tablet): a disclosure button + a small list of router links.
// The header menu scrolls horizontally (overflow hidden vertically), so the list is portaled to the
// body and placed under the button; it closes on Escape, outside click, scroll/resize and navigation.
export function ShopHistoryMenu({ history }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const buttonRef = useRef(null);
  const listRef = useRef(null);
  const location = useLocation();
  const close = useCallback(() => setOpen(false), []);
  useEffect(close, [location.pathname, location.search, close]);
  useEffect(() => {
    if (!open) return undefined;
    const rect = buttonRef.current?.getBoundingClientRect?.();
    if (rect) setPos({ top: rect.bottom + 6, left: Math.max(8, Math.min(rect.left, window.innerWidth - 228)) });
    const onKey = (e) => { if (e.key === "Escape") { setOpen(false); buttonRef.current?.focus?.(); } };
    const onDown = (e) => { if (!listRef.current?.contains(e.target) && !buttonRef.current?.contains(e.target)) setOpen(false); };
    window.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    window.addEventListener("resize", close);
    window.addEventListener("scroll", close, true);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", close, true);
    };
  }, [open, close]);
  const Icon = history.icon;
  const listId = "headershop-history-menu";
  return (
    <li className="headershop-menu-item headershop-menu-item--manage headershop-history">
      <button type="button" ref={buttonRef} className="headershop-menu-link headershop-menu-button headershop-manage-link" aria-expanded={open} aria-controls={listId} onClick={() => setOpen((v) => !v)}>
        {Icon && <Icon aria-hidden="true" />}<span className="headershop-manage-label">{history.label}</span><span aria-hidden="true">▾</span>
      </button>
      {open && typeof document !== "undefined" && createPortal(
        <ul id={listId} ref={listRef} className="headershop-history__list" style={pos ? { top: pos.top, left: pos.left } : undefined} aria-label={history.label}>
          {history.links.map((item) => {
            const ItemIcon = item.icon;
            return (
              <li key={item.key}>
                <Link to={item.to} className="headershop-history__link" title={item.title} onClick={close}>{ItemIcon && <ItemIcon aria-hidden="true" />}<span>{item.label}</span></Link>
              </li>
            );
          })}
        </ul>,
        document.body
      )}
    </li>
  );
}

export function ShopManagementMenuItems({ items, label, history = null }) {
  if (!items?.length && !history?.links?.length) return null;
  return (
    <>
      <li className="headershop-menu-item headershop-menu-sep" aria-hidden="true" title={label} />
      {items.map((item) => {
        const Icon = item.icon;
        const body = <><Icon aria-hidden="true" /><span className="headershop-manage-label">{item.label}</span></>;
        return (
          <li className="headershop-menu-item headershop-menu-item--manage" key={item.key}>
            {item.inert
              ? <span className="headershop-menu-link headershop-manage-link headershop-manage-link--inert" aria-disabled="true" title={item.title}>{body}</span>
              : <Link to={item.to} className="headershop-menu-link headershop-manage-link" title={item.title} aria-label={item.title}>{body}</Link>}
          </li>
        );
      })}
      {history?.links?.length > 0 && <ShopHistoryMenu history={history} />}
    </>
  );
}
